import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Button, Field, Icon, IconName, Input, Modal, Tooltip, triggerToast, uncapitalize, useConstCallback } from '@unbounded/unbounded-components'

import { formatToCurrencyNumber } from '~utils/currency'
import TextBlock from '~components/TextBlock'
import { NETWORK_FEE_IN_BOUNDS, TOKEN_SETTINGS } from '~routes/TokensPage/TokensPage.consts'
import Paths from '~routes/paths'

import useEthereumConnection from '../EthereumConnection'
import BalanceVestingTab from './BalanceVestingTab'

import * as Styled from './BalanceSettings.styles'

interface Props {
  onUpdateBalance?: () => void
}

const BalanceSettings: React.FC<Props> = ({ onUpdateBalance }) => {
  const { chain, zkChain } = TOKEN_SETTINGS
  const ethereum = useEthereumConnection(
    useMemo(
      () => ({
        chain,
        zkChain,
      }),
      [chain, zkChain]
    )
  )
  const {
    connection: ethereumConnection,
    queryBalance: ethereumQueryBalance,
    queryTokenAmount: ethereumQueryTokenAmount,
    bridgeL1TokensToL2: ethereumBridgeL1TokensToL2,
    open: ethereumOpen,
    close: ethereumClose,
  } = ethereum

  const [isBalanceError, setIsBalanceError] = useState(false)
  const [l2Balance, setL2Balance] = useState<number>()
  const [tokenBalance, setTokenBalance] = useState<number>()
  const [tokenSendAmount, setTokenSendAmount] = useState('')
  const [errorSendAmount, setErrorSendAmount] = useState<string>()
  const [isSendModalVisible, setIsSendModalVisible] = useState(false)
  const [isSendingTokens, setIsSendingTokens] = useState(false)
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const humanizeError = useConstCallback((message: string) => {
    let humanized = message

    if (humanized.includes('(action')) {
      humanized = humanized.split('(action')[0].trim()
    }

    if (humanized.includes('{')) {
      humanized = humanized.split('{')[0].trim()
    }

    humanized = uncapitalize(humanized)

    return humanized
  })

  const reloadL1Balance = useConstCallback(async () => {
    try {
      if (!ethereumConnection) {
        throw new Error("Couldn't query your balance: Ethereum not connected")
      }

      const newL2BalanceRaw = await ethereumQueryBalance(ethereumConnection.zkProvider)
      let divDecimals = BigInt(1)

      for (let i = 0; i < TOKEN_SETTINGS.decimals; i += 1) {
        divDecimals *= BigInt(10)
      }

      const newL2Balance = newL2BalanceRaw / divDecimals

      setL2Balance(Number(newL2Balance))
    } catch (e: any) {
      console.error('Failed to query your token balance', e)
      triggerToast('error', `Couldn't query your ${TOKEN_SETTINGS.name} balance on the Unbounded Network.`)
      setIsBalanceError(true)
    }
  })

  const reloadL2Balance = useConstCallback(async () => {
    try {
      if (!ethereumConnection) {
        throw new Error("Couldn't query your balance: Ethereum not connected")
      }

      const newTokenBalanceRaw = await ethereumQueryTokenAmount(ethereumConnection.provider, TOKEN_SETTINGS.tokenAddress)
      let divDecimals = BigInt(1)

      for (let i = 0; i < TOKEN_SETTINGS.decimals; i += 1) {
        divDecimals *= BigInt(10)
      }

      const newTokenBalance = BigInt(newTokenBalanceRaw) / divDecimals

      setTokenBalance(Number(newTokenBalance))
    } catch (e: any) {
      console.error('Failed to query your token balance', e)
      triggerToast('error', `Couldn't query your ${TOKEN_SETTINGS.name} balance on Ethereum.`)
      setIsBalanceError(true)
    }
  })

  const reloadTokenBalance = useConstCallback(() => {
    if (!ethereumConnection) {
      return
    }

    ;(async () => {
      setIsBalanceError(false)
      setL2Balance(undefined)
      setTokenBalance(undefined)

      await Promise.all([reloadL1Balance(), reloadL2Balance()])
    })()
  })

  useEffect(() => {
    ;(async () => {
      if (!ethereumConnection) {
        return
      }

      ethereumClose()

      reloadTokenBalance()
    })()
  }, [ethereumConnection, reloadTokenBalance])

  const handleSendTokens = useConstCallback(() => {
    setTokenSendAmount('')
    setIsSendModalVisible(true)
  })

  const handleCancelSendTokens = useConstCallback(() => {
    setIsSendModalVisible(false)
  })

  const maxToSend = Math.max(0, (tokenBalance ?? 0) - NETWORK_FEE_IN_BOUNDS)

  const onChangeTokenSendAmount = useConstCallback((value: string) => {
    setTokenSendAmount(value)

    if (!value) {
      setErrorSendAmount(undefined)
      return
    }

    const parsedValue = Number.parseFloat(value)

    if (Number.isNaN(parsedValue)) {
      setErrorSendAmount(`Amount of ${TOKEN_SETTINGS.name}s must be a number.`)
    } else if (parsedValue <= 0) {
      setErrorSendAmount(`Amount of ${TOKEN_SETTINGS.name}s must be above zero.`)
    } else if (parsedValue > maxToSend) {
      setErrorSendAmount(`Amount of ${TOKEN_SETTINGS.name}s must be below ${formatToCurrencyNumber(maxToSend)}.`)
    } else {
      setErrorSendAmount(undefined)
    }
  })

  const handleConfirmSendToken = useConstCallback(() => {
    ;(async () => {
      if (errorSendAmount) {
        return
      }

      if (!ethereumConnection) {
        return
      }

      setIsSendingTokens(true)

      let divDecimals = BigInt(1)

      for (let i = 0; i < TOKEN_SETTINGS.decimals; i += 1) {
        divDecimals *= BigInt(10)
      }

      const amountAsNumber = Number.parseFloat(tokenSendAmount) + NETWORK_FEE_IN_BOUNDS

      const sendAmount = BigInt(Math.floor(amountAsNumber * 1000) / 1000) * divDecimals

      try {
        await ethereumBridgeL1TokensToL2(
          ethereumConnection.provider,
          TOKEN_SETTINGS.zkBridgeHubAddress,
          TOKEN_SETTINGS.zkBridgeAddress,
          zkChain.chainId,
          TOKEN_SETTINGS.tokenAddress,
          ethereumConnection.account,
          sendAmount
        )
      } catch (e: any) {
        triggerToast(
          'error',
          `Couldn't transfer your ${TOKEN_SETTINGS.name} to the Unbounded Network: ${humanizeError(e.error || e.message) || 'please try again.'}`
        )
        setIsSendingTokens(false)
        return
      }

      if (!isMounted.current) {
        return
      }

      setIsSendingTokens(false)
      setIsSendModalVisible(false)
      reloadTokenBalance()

      if (onUpdateBalance) {
        onUpdateBalance()
      }
    })()
  })

  const handleUpdateTab = useConstCallback(() => {
    reloadTokenBalance()
    onUpdateBalance?.()
  })

  const isLoadingBalances = (tokenBalance === undefined || l2Balance === undefined) && !isBalanceError
  const renderBalanceLoadingOrError = useConstCallback((balance: number | undefined) => {
    if (isBalanceError && balance === undefined) {
      return 'Error'
    }
    if (balance === undefined) {
      return (
        <Styled.BalanceLoading>
          <Icon name={IconName.spinner} />
        </Styled.BalanceLoading>
      )
    }
    return `${formatToCurrencyNumber(balance)}\u00A0${TOKEN_SETTINGS.name}`
  })

  return (
    <>
      {!ethereumConnection && (
        <Styled.ConnectToEthereum>
          <Button onClick={ethereumOpen}>
            <Icon name={IconName.brand_ethereum} size="large" />
            &nbsp;Connect to Ethereum
          </Button>
        </Styled.ConnectToEthereum>
      )}
      {!ethereumConnection && (tokenBalance === undefined || l2Balance === undefined) && (
        <Styled.WhatsThis>
          <Link to={`${Paths.FAQ}#what-are-bound-tokens`}>
            <Icon name={IconName.circle_question} /> What&apos;s this?
          </Link>
        </Styled.WhatsThis>
      )}
      {ethereumConnection && (
        <Styled.BoundsBalanceWrapper>
          {!isLoadingBalances && (
            <Tooltip content="Refresh balance">
              <Styled.ReloadTokenBalance onClick={reloadTokenBalance}>
                <Icon name={IconName.refresh} />
              </Styled.ReloadTokenBalance>
            </Tooltip>
          )}
          <Styled.L1Balance>
            <Styled.BoundsBalanceAmount>
              <Styled.BoundsBalanceCaption>Ethereum:</Styled.BoundsBalanceCaption>
              {renderBalanceLoadingOrError(tokenBalance)}
            </Styled.BoundsBalanceAmount>
          </Styled.L1Balance>
          <Styled.BoundsBalanceBuyWrapper>
            <Styled.BoundsBalanceBuy>
              <Button color="primary" onClick={handleSendTokens} disabled={isSendingTokens || tokenBalance === undefined || l2Balance === undefined}>
                ↓ Transfer ↓
              </Button>
            </Styled.BoundsBalanceBuy>
          </Styled.BoundsBalanceBuyWrapper>
          <Styled.L2Balance>
            <Styled.BoundsBalanceAmount>
              <Styled.BoundsBalanceCaption>Unbounded Network:</Styled.BoundsBalanceCaption>
              {renderBalanceLoadingOrError(l2Balance)}
            </Styled.BoundsBalanceAmount>
          </Styled.L2Balance>
        </Styled.BoundsBalanceWrapper>
      )}
      <Styled.TabsPadding>{ethereumConnection && <BalanceVestingTab ethereum={ethereum} onUpdateBalance={handleUpdateTab} />}</Styled.TabsPadding>
      <Modal
        isVisible={isSendModalVisible}
        closeOnEsc={!isSendingTokens}
        closeOnClickOutside={!isSendingTokens}
        onClose={isSendingTokens ? undefined : handleCancelSendTokens}
        title={`Bridge your ${TOKEN_SETTINGS.name}s into the Unbounded Network`}
      >
        <Styled.SendTokensModal>
          <TextBlock type="info" variant="outline">
            Note that as part of the bridging process you will be prompted (once) to allow the bridge contract to work with your tokens; this is
            necessary for the operation to succeed. The bridge contract address should be as follows:
            <br />
            <br />
            {TOKEN_SETTINGS.zkBridgeAddress}
          </TextBlock>

          <Field
            label={`Amount of ${TOKEN_SETTINGS.name}s to bridge`}
            error={errorSendAmount}
            fixedCaption
            caption={`Max: ${formatToCurrencyNumber(maxToSend)} ${TOKEN_SETTINGS.name}s, network fee: ${formatToCurrencyNumber(
              NETWORK_FEE_IN_BOUNDS
            )} ${TOKEN_SETTINGS.name}s`}
          >
            <Input type="text" value={tokenSendAmount} onValueChange={onChangeTokenSendAmount} disabled={isSendingTokens} />
          </Field>
          <Styled.ModalActionButtons>
            <Button onClick={handleConfirmSendToken} isLoading={isSendingTokens} disabled={isSendingTokens || !!errorSendAmount}>
              <Icon name={IconName.brand_ethereum} size="large" />
              &nbsp;Transfer
            </Button>
          </Styled.ModalActionButtons>
        </Styled.SendTokensModal>
      </Modal>
    </>
  )
}

export default BalanceSettings
